import {
  compileRedactedRoutes,
  type RedactedRoute,
  redactPath,
} from "./redacted-routes";

// Page-wide rather than threaded through: the reporters that read the path
// run below LoggerProvider and hold a Logger, not the app's logging binding.
let redactedRoutes: readonly RedactedRoute[] = [];

/**
 * Declare the page's capability routes (see ./redacted-routes.ts) for
 * `currentPath` and `resourceLocation`. `createClientLogging` calls this with
 * its `redactedRoutes`; each call replaces the last.
 */
export function setRedactedRoutes(routes: readonly string[]): void {
  redactedRoutes = compileRedactedRoutes(routes);
}

const redactPagePath = (pathname: string): string =>
  redactPath(pathname, redactedRoutes);

/**
 * The route a browser event happened on, for joining performance events to
 * pages. Pathname only, never `search` or `hash`: a query string is where
 * campaign ids, tokens and typed-in text ride, and none of that belongs in a
 * `data` bag (policy §5). A declared capability route is reported as the
 * route, `/invite/[code]`, never the token in its path.
 *
 * `undefined` off-DOM (vitest's node projects, the prerender pass), which
 * drops the key at serialization rather than sending a misleading one.
 */
export function currentPath(): string | undefined {
  return typeof location === "undefined"
    ? undefined
    : redactPagePath(location.pathname);
}

/**
 * The same filter for a URL this code did not choose: web-vitals reports the
 * LCP resource's full absolute URL, search string included, and a signed
 * asset URL would carry its token into the dataset (policy §11: never log raw
 * request URLs).
 *
 * - same-origin: the bare pathname, redacted as `currentPath()` is
 * - cross-origin: `origin + pathname`, since which CDN served the resource is
 *   half the diagnosis and an origin carries no user data; never redacted,
 *   since the declared routes are this app's and that path is not
 * - `data:`, `blob:` and other schemes: the scheme alone, since their payload
 *   is in the path
 * - unparseable: `undefined`, since passing it through would defeat the filter
 */
export function resourceLocation(raw: string | undefined): string | undefined {
  if (raw === undefined || raw === "") return undefined;

  let parsed: URL;
  try {
    parsed = new URL(
      raw,
      typeof location === "undefined" ? undefined : location.href,
    );
  } catch {
    return undefined;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return parsed.protocol;
  }

  const sameOrigin =
    typeof location !== "undefined" && parsed.origin === location.origin;
  return sameOrigin
    ? redactPagePath(parsed.pathname)
    : `${parsed.origin}${parsed.pathname}`;
}
